// Devices & printing: POS terminals (POS ID + owner-set password sign-in),
// KDS screens (secret shown once on register or rotate), print stations
// with their typed transport (lan / cloud / usb / kds — fixed once
// created), kitchen routing per item, the print queue with a test print,
// the cash drawer log — and, for platform staff, the global printer
// registry the cloud stations point at.

import { useCallback, useEffect, useState } from 'react';
import { Pencil, Printer as PrinterIcon, RefreshCw, RotateCw, Trash2, X } from 'lucide-react';
import { api, dateTimeOf, label, type Me } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { Check, errMsg, Head, intOrNull, Modal, useAction } from './shared';
import type {
  CashDrawerEvent, ItemRouting, KdsDevice, KdsDeviceInput, KdsDeviceWithSecret, KdsType, PrintJob, PrintStation,
  PrintStationInput, Printer, PrinterWithSecret, StationType, Terminal, TerminalInput,
} from './types-b';

type Secret = { what: string; name: string; secret: string };

// ── print-station drafts ─────────────────────────────────────────────

type StationDraft = {
  stationId: string | null;
  stationType: StationType;
  name: string;
  ipAddress: string;
  port: string;
  printerModel: string;
  printerId: string;
  deviceName: string;
  kdsDeviceId: string;
};

const freshStation = (): StationDraft => ({
  stationId: null, stationType: 'lan', name: '', ipAddress: '', port: '9100', printerModel: '', printerId: '', deviceName: '', kdsDeviceId: '',
});

function stationDraft(s: PrintStation): StationDraft {
  const d = { ...freshStation(), stationId: s.stationId, stationType: s.stationType, name: s.name };
  switch (s.stationType) {
    case 'lan': return { ...d, ipAddress: s.ipAddress, port: String(s.port), printerModel: s.printerModel ?? '' };
    case 'cloud': return { ...d, printerId: s.printerId };
    case 'usb': return { ...d, deviceName: s.deviceName, printerModel: s.printerModel ?? '' };
    case 'kds': return { ...d, kdsDeviceId: s.kdsDeviceId };
  }
}

function stationBody(d: StationDraft): PrintStationInput {
  const name = d.name.trim();
  switch (d.stationType) {
    case 'lan': return { name, stationType: 'lan', ipAddress: d.ipAddress.trim(), port: intOrNull(d.port) ?? 0, printerModel: d.printerModel.trim() || null };
    case 'cloud': return { name, stationType: 'cloud', printerId: d.printerId.trim() };
    case 'usb': return { name, stationType: 'usb', deviceName: d.deviceName.trim(), printerModel: d.printerModel.trim() || null };
    case 'kds': return { name, stationType: 'kds', kdsDeviceId: d.kdsDeviceId };
  }
}

const STATION_TYPES: { value: StationType; label: string }[] = [
  { value: 'lan', label: 'Network (LAN)' },
  { value: 'cloud', label: 'Cloud printer' },
  { value: 'usb', label: 'USB' },
  { value: 'kds', label: 'Kitchen screen' },
];

// ── KDS drafts ───────────────────────────────────────────────────────

type KdsDraft = { name: string; kdsType: KdsType; target: string; warning: string; active: boolean };
const freshKds = (): KdsDraft => ({ name: '', kdsType: 'line', target: '', warning: '', active: true });
const kdsBody = (d: KdsDraft, itemIds: string[], rotateSecret = false): KdsDeviceInput => ({
  name: d.name.trim(), kdsType: d.kdsType, targetPrepMinutes: intOrNull(d.target), warningPrepMinutes: intOrNull(d.warning), itemIds, active: d.active, rotateSecret,
});
const kdsDraft = (k: KdsDevice): KdsDraft => ({
  name: k.name, kdsType: k.kdsType, target: k.targetPrepMinutes?.toString() ?? '', warning: k.warningPrepMinutes?.toString() ?? '', active: k.active,
});

const JOB_STATUSES = [
  { value: 'pending,failed', label: 'Pending & failed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending,failed,completed', label: 'All' },
];

const todayIn = (tz?: string): string => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export function Devices() {
  const base = useBase();
  const { store } = useScope();
  const tz = store?.timezone;

  const [terminals, setTerminals] = useState<Terminal[] | null>(null);
  const [screens, setScreens] = useState<KdsDevice[] | null>(null);
  const [stations, setStations] = useState<PrintStation[] | null>(null);
  const [items, setItems] = useState<{ itemId: string; name: string }[]>([]);
  const [printers, setPrinters] = useState<Printer[] | null>(null);
  const [isSuper, setIsSuper] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [secret, setSecret] = useState<Secret | null>(null);

  const loadTerminals = useCallback(async () => setTerminals(await api.get<Terminal[]>(`${base}/terminals`)), [base]);
  const loadScreens = useCallback(async () => setScreens(await api.get<KdsDevice[]>(`${base}/kds-devices`)), [base]);
  const loadStations = useCallback(async () => setStations(await api.get<PrintStation[]>(`${base}/print-stations`)), [base]);
  const loadPrinters = useCallback(async () => setPrinters(await api.get<Printer[]>('/printers')), []);

  useEffect(() => {
    void (async () => {
      try {
        const [, , , its, me] = await Promise.all([
          loadTerminals(), loadScreens(), loadStations(),
          api.get<{ itemId: string; name: string }[]>(`${base}/items`),
          api.get<Me>('/me'),
        ]);
        setItems(its);
        setIsSuper(me.isSuperAdmin);
        if (me.isSuperAdmin) await loadPrinters();
        setLoadErr(null);
      } catch (e) {
        setLoadErr(errMsg(e));
      }
    })();
  }, [base, loadTerminals, loadScreens, loadStations, loadPrinters]);

  // the print queue reloads after a test print
  const [jobsTick, setJobsTick] = useState(0);

  return (
    <>
      <Head title="Devices & printing" />
      {loadErr && <div className="err" role="alert">{loadErr}</div>}
      <div className="set-cols">
        <div className="set-stack">
          <TerminalsPanel base={base} tz={tz} terminals={terminals} stations={stations} reload={loadTerminals} />
          <ScreensPanel base={base} screens={screens} reload={loadScreens} onSecret={setSecret} />
          <CashDrawerPanel base={base} tz={tz} terminals={terminals} />
        </div>
        <div className="set-stack">
          <StationsPanel base={base} stations={stations} screens={screens} printers={printers} reload={loadStations}
            onTestPrint={() => setJobsTick((t) => t + 1)} />
          <RoutingPanel base={base} items={items} stations={stations} screens={screens} onSaved={loadStations} />
          <JobsPanel base={base} tz={tz} stations={stations} tick={jobsTick} />
          {isSuper && <PrintersPanel printers={printers} reload={loadPrinters} onSecret={setSecret} />}
        </div>
      </div>

      {secret && (
        <Modal title={`${secret.what} secret — shown once`} onClose={() => setSecret(null)}>
          <div className="kv">
            Pairing secret for <b>{secret.name}</b>. Copy it now — it cannot be shown again (rotate it to mint a new one).
          </div>
          <div className="secret-box" data-secret>{secret.secret}</div>
        </Modal>
      )}
    </>
  );
}

// ── terminals ────────────────────────────────────────────────────────

function TerminalsPanel(props: { base: string; tz?: string; terminals: Terminal[] | null; stations: PrintStation[] | null; reload: () => Promise<void> }) {
  const { base, tz, terminals, stations, reload } = props;
  const act = useAction();
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TerminalInput>({ name: '', cashDrawerStationId: null, active: true });
  const [pwd, setPwd] = useState<{ terminal: Terminal; value: string } | null>(null);
  const pwdAct = useAction();
  const run = (fn: () => Promise<unknown>) => void act.run(async () => { await fn(); await reload(); });
  const put = (t: Terminal, body: TerminalInput) => api.put<Terminal>(`${base}/terminals/${t.terminalId}`, { ...body, posId: t.posId });
  const stationName = (id: string | null) => stations?.find((s) => s.stationId === id)?.name ?? id;

  return (
    <div className="panel" data-section="terminals">
      <h2>POS terminals</h2>
      {terminals === null && <div className="loading">Loading…</div>}
      {terminals?.length === 0 && <div className="loading">No terminals registered.</div>}
      {terminals?.map((t) => (
        <div key={t.terminalId} className="rowline" style={{ flexWrap: 'wrap' }} data-terminal={t.posId ?? t.terminalId}>
          {editId === t.terminalId ? (
            <>
              <input className="input" style={{ maxWidth: 180 }} aria-label="Terminal name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              <select className="input" style={{ maxWidth: 180 }} aria-label="Cash drawer station" value={draft.cashDrawerStationId ?? ''}
                onChange={(e) => setDraft({ ...draft, cashDrawerStationId: e.target.value || null })}>
                <option value="">No cash drawer</option>
                {stations?.map((s) => <option key={s.stationId} value={s.stationId}>{s.name}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" disabled={act.busy || !draft.name.trim()}
                onClick={() => run(async () => { await put(t, { ...draft, name: draft.name.trim() }); setEditId(null); })}>Save terminal</button>
              <button className="btn btn-ghost btn-sm" title="Cancel edit" onClick={() => setEditId(null)}><X size={14} /></button>
            </>
          ) : (
            <>
              <span className={`term-dot ${!t.active ? 'term-dot-off' : t.lastSeenAt ? 'term-dot-ok' : 'term-dot-idle'}`}
                title={!t.active ? 'Inactive' : t.lastSeenAt ? 'Has signed in' : 'Never signed in'} />
              <div className="grow">
                <b style={t.active ? undefined : { color: 'var(--ink-3)' }}>{t.name}</b>
                <span className="sub">
                  {t.posId && <span className="posid" data-pos-id>{t.posId}</span>}
                  {t.docketPrefix ? ` · dockets ${t.docketPrefix}` : ''}
                  {' · '}
                  <span data-status={t.hasPassword ? 'password_set' : 'no_password'}>
                    {!t.active ? 'inactive' : t.lastSeenAt ? `last seen ${dateTimeOf(t.lastSeenAt, tz)}` : t.hasPassword ? 'password set · never signed in' : 'no password set'}
                  </span>
                  {t.cashDrawerStationId ? ` · drawer on ${stationName(t.cashDrawerStationId)}` : ''}
                </span>
              </div>
              {t.active && (
                <button className={`btn btn-sm ${t.hasPassword ? 'btn-outline' : 'btn-primary'}`} disabled={act.busy}
                  onClick={() => setPwd({ terminal: t, value: '' })}>
                  {t.hasPassword ? 'Change password' : 'Set password'}
                </button>
              )}
              <button className="btn btn-ghost btn-sm" title={`Edit ${t.name}`} disabled={act.busy}
                onClick={() => { setEditId(t.terminalId); setDraft({ name: t.name, cashDrawerStationId: t.cashDrawerStationId, active: t.active }); }}>
                <Pencil size={14} />
              </button>
              <button className="btn btn-ghost btn-sm" disabled={act.busy}
                onClick={() => run(() => put(t, { name: t.name, cashDrawerStationId: t.cashDrawerStationId, active: !t.active }))}>
                {t.active ? 'Deactivate' : 'Activate'}
              </button>
            </>
          )}
        </div>
      ))}
      <div className="frow" style={{ marginTop: 12 }}>
        <div className="field">
          <label htmlFor="new-terminal">New terminal</label>
          <input id="new-terminal" className="input" placeholder="Front counter" aria-label="New terminal name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </div>
        <button className="btn btn-outline" disabled={act.busy || !newName.trim()}
          onClick={() => run(async () => { await api.post<Terminal>(`${base}/terminals`, { name: newName.trim(), cashDrawerStationId: null, active: true }); setNewName(''); })}>
          Register terminal
        </button>
      </div>
      <div className="hint" style={{ marginTop: 6 }}>
        Registering assigns the POS ID. Set a password, then sign in on the POS with both; changing the password signs the current device out.
      </div>
      {act.error && <div className="err" role="alert">{act.error}</div>}

      {pwd && (
        <Modal title={`${pwd.terminal.hasPassword ? 'Change' : 'Set'} password — ${pwd.terminal.name}`} onClose={() => setPwd(null)}>
          <div className="kv">
            {pwd.terminal.posId ? <>POS ID <b>{pwd.terminal.posId}</b>. </> : null}
            Choose a password your staff can type on a touch keyboard.
          </div>
          <div className="field" style={{ margin: '12px 0 6px' }}>
            <label htmlFor="terminal-password">Password</label>
            <input id="terminal-password" className="input" autoFocus aria-label="Terminal password" placeholder="at least 6 characters" value={pwd.value}
              onChange={(e) => setPwd({ ...pwd, value: e.target.value })} />
          </div>
          <div className="hint">Shown while you type so you can copy it down.</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={pwdAct.busy || !pwd.value.trim()}
              onClick={() => void pwdAct.run(async () => {
                await api.put<Terminal>(`${base}/terminals/${pwd.terminal.terminalId}/password`, { password: pwd.value });
                setPwd(null);
                await reload();
              })}>
              {pwdAct.busy ? 'Saving…' : 'Save password'}
            </button>
          </div>
          {pwdAct.error && <div className="err" role="alert">{pwdAct.error}</div>}
        </Modal>
      )}
    </div>
  );
}

// ── KDS screens ──────────────────────────────────────────────────────

function KdsFields(props: { d: KdsDraft; onChange: (d: KdsDraft) => void; prefix: string }) {
  const { d, onChange, prefix } = props;
  return (
    <>
      <input className="input" style={{ maxWidth: 170 }} placeholder="Grill" aria-label={`${prefix} screen name`} value={d.name} onChange={(e) => onChange({ ...d, name: e.target.value })} />
      <select className="input" style={{ maxWidth: 130 }} aria-label={`${prefix} screen type`} value={d.kdsType} onChange={(e) => onChange({ ...d, kdsType: e.target.value as KdsType })}>
        <option value="line">Line</option>
        <option value="expediter">Expediter</option>
      </select>
      <input className="input" style={{ width: 84 }} type="number" min={0} placeholder="target" title="Target prep minutes" aria-label={`${prefix} target minutes`} value={d.target} onChange={(e) => onChange({ ...d, target: e.target.value })} />
      <input className="input" style={{ width: 84 }} type="number" min={0} placeholder="warn" title="Warning prep minutes" aria-label={`${prefix} warning minutes`} value={d.warning} onChange={(e) => onChange({ ...d, warning: e.target.value })} />
    </>
  );
}

function ScreensPanel(props: { base: string; screens: KdsDevice[] | null; reload: () => Promise<void>; onSecret: (s: Secret) => void }) {
  const { base, screens, reload, onSecret } = props;
  const act = useAction();
  const [fresh, setFresh] = useState<KdsDraft>(freshKds());
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<KdsDraft>(freshKds());
  const run = (fn: () => Promise<unknown>) => void act.run(async () => { await fn(); await reload(); });
  const reveal = (d: KdsDeviceWithSecret) => { if (d.secret) onSecret({ what: 'Kitchen screen', name: d.name, secret: d.secret }); };

  return (
    <div className="panel" data-section="kds-devices">
      <h2>Kitchen screens (KDS)</h2>
      {screens === null && <div className="loading">Loading…</div>}
      {screens?.length === 0 && <div className="loading">No screens registered.</div>}
      {screens?.map((k) => (
        <div key={k.deviceId} className="rowline" style={{ flexWrap: 'wrap' }}>
          {editId === k.deviceId ? (
            <>
              <KdsFields d={draft} onChange={setDraft} prefix="Edit" />
              <Check label="Active" checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} />
              <button className="btn btn-primary btn-sm" disabled={act.busy || !draft.name.trim()}
                onClick={() => run(async () => { await api.put(`${base}/kds-devices/${k.deviceId}`, kdsBody(draft, k.itemIds)); setEditId(null); })}>Save screen</button>
              <button className="btn btn-ghost btn-sm" title="Cancel edit" onClick={() => setEditId(null)}><X size={14} /></button>
            </>
          ) : (
            <>
              <div className="grow">
                <b style={k.active ? undefined : { color: 'var(--ink-3)' }}>{k.name}{k.active ? '' : ' (retired)'}</b>
                <span className="sub">
                  {k.kdsType === 'expediter' ? 'Expediter' : 'Line'} screen
                  {k.targetPrepMinutes !== null ? ` · target ${k.targetPrepMinutes} min` : ''}
                  {k.warningPrepMinutes !== null ? ` · warn ${k.warningPrepMinutes} min` : ''}
                  {k.itemIds.length ? ` · ${k.itemIds.length} item${k.itemIds.length === 1 ? '' : 's'} routed` : ''}
                  {k.lastSeenAt ? ` · last seen ${dateTimeOf(k.lastSeenAt)}` : ' · never paired'}
                </span>
              </div>
              <button className="btn btn-ghost btn-sm" title={`Edit ${k.name}`} disabled={act.busy}
                onClick={() => { setEditId(k.deviceId); setDraft(kdsDraft(k)); }}><Pencil size={14} /></button>
              <button className="btn btn-ghost btn-sm" title={`Rotate secret for ${k.name}`} disabled={act.busy}
                onClick={() => {
                  if (!confirm(`Mint a new secret for “${k.name}”? The screen signs in again with it.`)) return;
                  run(async () => reveal(await api.put<KdsDeviceWithSecret>(`${base}/kds-devices/${k.deviceId}`, kdsBody(kdsDraft(k), k.itemIds, true))));
                }}><RotateCw size={14} /></button>
              {k.active && (
                <button className="btn btn-ghost btn-sm" title={`Retire ${k.name}`} disabled={act.busy}
                  onClick={() => { if (confirm(`Retire the screen “${k.name}”?`)) run(() => api.del(`${base}/kds-devices/${k.deviceId}`)); }}>
                  <Trash2 size={14} />
                </button>
              )}
            </>
          )}
        </div>
      ))}
      <div className="rowline" style={{ flexWrap: 'wrap', marginTop: 8 }}>
        <KdsFields d={fresh} onChange={setFresh} prefix="New" />
        <button className="btn btn-outline btn-sm" disabled={act.busy || !fresh.name.trim()}
          onClick={() => run(async () => { reveal(await api.post<KdsDeviceWithSecret>(`${base}/kds-devices`, kdsBody(fresh, []))); setFresh(freshKds()); })}>
          Register screen
        </button>
      </div>
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

// ── print stations ───────────────────────────────────────────────────

function transportText(s: PrintStation, screens: KdsDevice[] | null, printers: Printer[] | null): string {
  switch (s.stationType) {
    case 'lan': return `${s.ipAddress}:${s.port}${s.printerModel ? ` · ${s.printerModel}` : ''}`;
    case 'cloud': return `cloud printer ${printers?.find((p) => p.printerId === s.printerId)?.name ?? s.printerId}`;
    case 'usb': return `${s.deviceName}${s.printerModel ? ` · ${s.printerModel}` : ''}`;
    case 'kds': return `screen ${screens?.find((k) => k.deviceId === s.kdsDeviceId)?.name ?? s.kdsDeviceId}`;
  }
}

function StationsPanel(props: {
  base: string; stations: PrintStation[] | null; screens: KdsDevice[] | null; printers: Printer[] | null;
  reload: () => Promise<void>; onTestPrint: () => void;
}) {
  const { base, stations, screens, printers, reload, onTestPrint } = props;
  const act = useAction();
  const [draft, setDraft] = useState<StationDraft | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const run = (fn: () => Promise<unknown>) => void act.run(async () => { await fn(); await reload(); });
  const d = draft;

  return (
    <div className="panel" data-section="print-stations">
      <h2>Print stations</h2>
      {stations === null && <div className="loading">Loading…</div>}
      {stations?.length === 0 && <div className="loading">No print stations yet.</div>}
      {stations?.map((s) => (
        <div key={s.stationId} className="rowline" data-station-type={s.stationType}>
          <span className="src">{s.stationType}</span>
          <div className="grow">
            <b>{s.name}</b>
            <span className="sub">{transportText(s, screens, printers)} · {s.itemCount} item{s.itemCount === 1 ? '' : 's'} routed</span>
          </div>
          {sent === s.stationId && <span className="saved-flash">Queued</span>}
          <button className="btn btn-ghost btn-sm" title={`Test print ${s.name}`} disabled={act.busy}
            onClick={() => void act.run(async () => {
              await api.post<PrintJob>(`${base}/print-jobs`, { stationId: s.stationId, jobType: 'test' });
              setSent(s.stationId);
              onTestPrint();
            })}>
            <PrinterIcon size={14} />
          </button>
          <button className="btn btn-ghost btn-sm" title={`Edit ${s.name}`} disabled={act.busy} onClick={() => setDraft(stationDraft(s))}><Pencil size={14} /></button>
          <button className="btn btn-ghost btn-sm" title={`Delete ${s.name}`} disabled={act.busy}
            onClick={() => { if (confirm(`Delete the station “${s.name}”?`)) run(async () => { await api.del(`${base}/print-stations/${s.stationId}`); setDraft((x) => (x?.stationId === s.stationId ? null : x)); }); }}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {!d && (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-outline" onClick={() => setDraft(freshStation())}>Add print station</button>
        </div>
      )}
      {d && (
        <div className="set-stack" style={{ marginTop: 14, gap: 12 }} data-section="station-form">
          <div className="overline">{d.stationId ? 'Edit station' : 'New station'}</div>
          <div className="frow">
            <div className="field narrow" style={{ flexBasis: 170 }}>
              <label htmlFor="station-type">Transport</label>
              <select id="station-type" className="input" aria-label="Station type" value={d.stationType} disabled={d.stationId !== null}
                title={d.stationId ? 'A station keeps its transport; make a new station to change it' : undefined}
                onChange={(e) => setDraft({ ...d, stationType: e.target.value as StationType })}>
                {STATION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="station-name">Name</label>
              <input id="station-name" className="input" placeholder="Kitchen" aria-label="Station name" value={d.name} onChange={(e) => setDraft({ ...d, name: e.target.value })} />
            </div>
          </div>
          {d.stationType === 'lan' && (
            <div className="frow">
              <div className="field">
                <label htmlFor="station-ip">IP address</label>
                <input id="station-ip" className="input" placeholder="192.168.1.50" aria-label="IP address" value={d.ipAddress} onChange={(e) => setDraft({ ...d, ipAddress: e.target.value })} />
              </div>
              <div className="field narrow">
                <label htmlFor="station-port">Port</label>
                <input id="station-port" className="input" type="number" aria-label="Port" value={d.port} onChange={(e) => setDraft({ ...d, port: e.target.value })} />
              </div>
            </div>
          )}
          {(d.stationType === 'lan' || d.stationType === 'usb') && (
            <div className="field">
              <label htmlFor="station-model">Printer model (optional)</label>
              <input id="station-model" className="input" placeholder="Epson TM-T82" aria-label="Printer model" value={d.printerModel} onChange={(e) => setDraft({ ...d, printerModel: e.target.value })} />
            </div>
          )}
          {d.stationType === 'usb' && (
            <div className="field">
              <label htmlFor="station-usb">USB device name</label>
              <input id="station-usb" className="input" placeholder="usb0" aria-label="USB device name" value={d.deviceName} onChange={(e) => setDraft({ ...d, deviceName: e.target.value })} />
            </div>
          )}
          {d.stationType === 'cloud' && (
            <div className="field">
              <label htmlFor="station-printer">Cloud printer</label>
              {printers ? (
                <select id="station-printer" className="input" aria-label="Cloud printer" value={d.printerId} onChange={(e) => setDraft({ ...d, printerId: e.target.value })}>
                  <option value="">Choose a registered printer…</option>
                  {printers.map((p) => <option key={p.printerId} value={p.printerId}>{p.name}</option>)}
                </select>
              ) : (
                <input id="station-printer" className="input" placeholder="prn_…" aria-label="Cloud printer" value={d.printerId} onChange={(e) => setDraft({ ...d, printerId: e.target.value })} />
              )}
              <div className="hint" style={{ marginTop: 4 }}>The printer’s registry ID, from the printer’s setup card.</div>
            </div>
          )}
          {d.stationType === 'kds' && (
            <div className="field">
              <label htmlFor="station-kds">Kitchen screen</label>
              <select id="station-kds" className="input" aria-label="Kitchen screen" value={d.kdsDeviceId} onChange={(e) => setDraft({ ...d, kdsDeviceId: e.target.value })}>
                <option value="">Choose a screen…</option>
                {screens?.filter((k) => k.active).map((k) => <option key={k.deviceId} value={k.deviceId}>{k.name}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" disabled={act.busy || !d.name.trim()}
              onClick={() => run(async () => {
                if (d.stationId) await api.put<PrintStation>(`${base}/print-stations/${d.stationId}`, stationBody(d));
                else await api.post<PrintStation>(`${base}/print-stations`, stationBody(d));
                setDraft(null);
              })}>
              {act.busy ? 'Saving…' : d.stationId ? 'Save station' : 'Create station'}
            </button>
            <button className="btn btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

// ── kitchen routing ──────────────────────────────────────────────────

function RoutingPanel(props: {
  base: string; items: { itemId: string; name: string }[]; stations: PrintStation[] | null; screens: KdsDevice[] | null; onSaved: () => Promise<void>;
}) {
  const { base, items, stations, screens, onSaved } = props;
  const [itemId, setItemId] = useState('');
  const [routing, setRouting] = useState<ItemRouting | null>(null);
  const act = useAction();

  useEffect(() => {
    setRouting(null);
    if (!itemId) return;
    let live = true;
    api.get<ItemRouting>(`${base}/items/${itemId}/routing`).then(
      (r) => { if (live) setRouting(r); },
      (e) => { if (live) act.setError(errMsg(e)); },
    );
    return () => { live = false; };
  }, [base, itemId, act.setError]);

  const route = (stationId: string) => routing?.stations.find((s) => s.stationId === stationId);
  const setRoute = (stationId: string, on: boolean, isTagAlong = false) => {
    if (!routing) return;
    const rest = routing.stations.filter((s) => s.stationId !== stationId);
    setRouting({ ...routing, stations: on ? [...rest, { stationId, isTagAlong }] : rest });
  };
  const setScreen = (deviceId: string, on: boolean) => {
    if (!routing) return;
    const rest = routing.kdsDeviceIds.filter((d) => d !== deviceId);
    setRouting({ ...routing, kdsDeviceIds: on ? [...rest, deviceId] : rest });
  };

  return (
    <div className="panel" data-section="routing">
      <h2>Kitchen routing</h2>
      <div className="hint" style={{ margin: '6px 0 8px' }}>
        Where an item prints and shows. A tag-along route copies the item onto another station’s docket for context.
      </div>
      <div className="field" style={{ maxWidth: 320 }}>
        <label htmlFor="routing-item">Menu item</label>
        <select id="routing-item" className="input" aria-label="Routed item" value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">Choose an item…</option>
          {items.map((i) => <option key={i.itemId} value={i.itemId}>{i.name}</option>)}
        </select>
      </div>
      {itemId && !routing && !act.error && <div className="loading">Loading…</div>}
      {routing && (
        <div style={{ marginTop: 10 }}>
          {stations?.length === 0 && <div className="loading">No stations to route to.</div>}
          {stations?.map((s) => {
            const r = route(s.stationId);
            return (
              <div key={s.stationId} className="rowline">
                <label className="checkline grow">
                  <input type="checkbox" aria-label={`Route to ${s.name}`} checked={!!r} onChange={(e) => setRoute(s.stationId, e.target.checked)} />
                  {s.name}
                </label>
                {r && (
                  <label className="checkline">
                    <input type="checkbox" aria-label={`Tag-along on ${s.name}`} checked={r.isTagAlong} onChange={(e) => setRoute(s.stationId, true, e.target.checked)} />
                    tag-along
                  </label>
                )}
              </div>
            );
          })}
          {screens?.filter((k) => k.active).map((k) => (
            <div key={k.deviceId} className="rowline">
              <label className="checkline grow">
                <input type="checkbox" aria-label={`Show on ${k.name}`} checked={routing.kdsDeviceIds.includes(k.deviceId)} onChange={(e) => setScreen(k.deviceId, e.target.checked)} />
                {k.name} <span className="sub">(screen)</span>
              </label>
            </div>
          ))}
          <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" disabled={act.busy}
              onClick={() => void act.run(async () => { setRouting(await api.put<ItemRouting>(`${base}/items/${itemId}/routing`, routing)); await onSaved(); })}>
              Save routing
            </button>
            {act.saved && <span className="saved-flash">Saved</span>}
          </div>
        </div>
      )}
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

// ── the print queue ──────────────────────────────────────────────────

function JobsPanel(props: { base: string; tz?: string; stations: PrintStation[] | null; tick: number }) {
  const { base, tz, stations, tick } = props;
  const [station, setStation] = useState('');
  const [status, setStatus] = useState('pending,failed');
  const [jobs, setJobs] = useState<PrintJob[] | null>(null);
  const act = useAction();
  const load = useCallback(() => void act.run(async () => {
    setJobs(await api.get<PrintJob[]>(`${base}/print-jobs`, { station: station || undefined, status }));
  }), [base, station, status, act.run]);
  useEffect(() => { load(); }, [load, tick]);
  const stationName = (id: string) => stations?.find((s) => s.stationId === id)?.name ?? id;

  return (
    <div className="panel" data-section="print-jobs">
      <h2>Print queue</h2>
      <div className="frow" style={{ margin: '8px 0' }}>
        <div className="field">
          <label htmlFor="jobs-station">Station</label>
          <select id="jobs-station" className="input" aria-label="Job station" value={station} onChange={(e) => setStation(e.target.value)}>
            <option value="">All stations</option>
            {stations?.map((s) => <option key={s.stationId} value={s.stationId}>{s.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="jobs-status">Status</label>
          <select id="jobs-status" className="input" aria-label="Job status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <button className="btn btn-ghost btn-sm" title="Refresh print queue" disabled={act.busy} onClick={load}><RefreshCw size={14} /></button>
      </div>
      {jobs?.length === 0 && <div className="loading">No jobs.</div>}
      {jobs?.map((j) => (
        <div key={j.jobId} className="rowline" data-job-type={j.jobType} data-status={j.status}>
          <div className="grow">
            <b>{label(j.jobType)} · {stationName(j.stationId)}</b>
            <span className="sub">
              queued {dateTimeOf(j.enqueuedAt, tz)}
              {j.attemptCount ? ` · ${j.attemptCount} attempt${j.attemptCount === 1 ? '' : 's'}` : ''}
              {j.failReason ? ` · ${j.failReason}` : ''}
            </span>
          </div>
          <span className={`chip ${j.status === 'completed' ? 'chip-completed' : j.status === 'failed' ? 'chip-rejected' : 'chip-pending'}`}>{j.status}</span>
        </div>
      ))}
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

// ── cash drawer ──────────────────────────────────────────────────────

function CashDrawerPanel(props: { base: string; tz?: string; terminals: Terminal[] | null }) {
  const { base, tz, terminals } = props;
  const [date, setDate] = useState('');
  const [events, setEvents] = useState<CashDrawerEvent[] | null>(null);
  const act = useAction();
  useEffect(() => { if (!date && tz) setDate(todayIn(tz)); }, [date, tz]);
  useEffect(() => {
    if (!date) return;
    void act.run(async () => { setEvents(await api.get<CashDrawerEvent[]>(`${base}/cash-drawer/opens`, { businessDate: date })); });
  }, [base, date, act.run]);
  return (
    <div className="panel" data-section="cash-drawer">
      <h2>Cash drawer</h2>
      <div className="field narrow" style={{ maxWidth: 180, margin: '8px 0' }}>
        <label htmlFor="drawer-date">Business date</label>
        <input id="drawer-date" className="input" type="date" aria-label="Drawer business date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      {events?.length === 0 && <div className="loading">No drawer opens that day.</div>}
      {events?.map((e) => (
        <div key={e.eventId} className="rowline">
          <span className="tm">{dateTimeOf(e.occurredAt, tz)}</span>
          <div className="grow">
            <b>{e.reason ?? 'No-sale open'}</b>
            <span className="sub">
              {e.posTerminalId ? `terminal ${terminals?.find((t) => t.terminalId === e.posTerminalId)?.name ?? e.posTerminalId}` : 'no terminal'}
              {e.employeeId ? ` · employee ${e.employeeId}` : ''}
            </span>
          </div>
        </div>
      ))}
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

// ── the global printer registry (platform staff) ─────────────────────

function PrintersPanel(props: { printers: Printer[] | null; reload: () => Promise<void>; onSecret: (s: Secret) => void }) {
  const { printers, reload, onSecret } = props;
  const act = useAction();
  const [name, setName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const run = (fn: () => Promise<unknown>) => void act.run(async () => { await fn(); await reload(); });
  const reveal = (p: PrinterWithSecret) => { if (p.secret) onSecret({ what: 'Cloud printer', name: p.name, secret: p.secret }); };
  return (
    <div className="panel" data-section="printers">
      <h2>Cloud printer registry</h2>
      <div className="hint" style={{ margin: '6px 0 8px' }}>Platform staff only — every store’s cloud stations point at printers registered here.</div>
      {printers?.length === 0 && <div className="loading">No printers registered.</div>}
      {printers?.map((p) => (
        <div key={p.printerId} className="rowline">
          {editId === p.printerId ? (
            <>
              <input className="input grow" aria-label="Printer name" value={editName} onChange={(e) => setEditName(e.target.value)} />
              <button className="btn btn-primary btn-sm" disabled={act.busy || !editName.trim()}
                onClick={() => run(async () => { await api.put(`/printers/${p.printerId}`, { name: editName.trim(), rotateSecret: false }); setEditId(null); })}>Save printer</button>
              <button className="btn btn-ghost btn-sm" title="Cancel edit" onClick={() => setEditId(null)}><X size={14} /></button>
            </>
          ) : (
            <>
              <div className="grow">
                <b>{p.name}</b>
                <span className="sub"><span className="posid">{p.printerId}</span>{p.lastSeenAt ? ` · last seen ${dateTimeOf(p.lastSeenAt)}` : ' · never paired'}</span>
              </div>
              <button className="btn btn-ghost btn-sm" title={`Rename ${p.name}`} disabled={act.busy} onClick={() => { setEditId(p.printerId); setEditName(p.name); }}><Pencil size={14} /></button>
              <button className="btn btn-ghost btn-sm" title={`Rotate secret for ${p.name}`} disabled={act.busy}
                onClick={() => { if (confirm(`Mint a new secret for “${p.name}”?`)) run(async () => reveal(await api.put<PrinterWithSecret>(`/printers/${p.printerId}`, { name: p.name, rotateSecret: true }))); }}>
                <RotateCw size={14} />
              </button>
              <button className="btn btn-ghost btn-sm" title={`Delete ${p.name}`} disabled={act.busy}
                onClick={() => { if (confirm(`Delete the printer “${p.name}”?`)) run(() => api.del(`/printers/${p.printerId}`)); }}>
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      ))}
      <div className="frow" style={{ marginTop: 12 }}>
        <div className="field">
          <label htmlFor="new-printer">New printer</label>
          <input id="new-printer" className="input" placeholder="Kitchen cloud" aria-label="New printer name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <button className="btn btn-outline" disabled={act.busy || !name.trim()}
          onClick={() => run(async () => { reveal(await api.post<PrinterWithSecret>('/printers', { name: name.trim() })); setName(''); })}>
          Register printer
        </button>
      </div>
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}
